import axios from "axios";

const REQUIRED_STATUS = "2fa_required";
const FAILED_STATUS = "2fa_failed";

const FAILURE_MESSAGES = {
    invalid_code: "Code invalide, réessayez.",
    code_expired: "Code expiré. Cliquez sur « Renvoyer le code ».",
    too_many_attempts: "Trop de tentatives. Réessayez plus tard."
};

/**
 * Returns a French message for a given 2FA failure code.
 * @param {string} code - The failure code from the API
 * @returns {string} Human-readable message
 */
function failureMessage(code) {
    return FAILURE_MESSAGES[code] || "Échec de la vérification 2FA.";
}

/**
 * Extracts the 2FA failure code from an axios error, if any.
 * @param {Object} error - The axios error thrown by a rejected request
 * @returns {string|null} invalid_code|code_expired|too_many_attempts, or null
 */
function resolveFailureCode(error) {
    const data = error?.response?.data;
    if (data && typeof data === "object" && data.status === FAILED_STATUS) {
        return data.error || "invalid_code";
    }
    return null;
}

/**
 * Replays the original request adding the X-2FA-Code header.
 * @param {Object} config - The original axios request config
 * @param {string} code - The OTP code entered by the admin
 * @returns {Promise<Object>} The axios response
 */
function replayWithCode(config, code) {
    const headers = { ...(config.headers || {}), "X-2FA-Code": code };
    return axios({ ...config, headers });
}

/**
 * Runs a request and, when the server asks for 2FA, opens the modal and returns
 * a promise resolved once the replay with a valid code succeeds.
 * @param {Object} config - The axios request config to run (and later replay)
 * @param {Object} ctx - { setOpen, setError, flowRef }
 * @returns {Promise<Object>} The final axios response
 */
async function startFlow(config, ctx) {
    const first = await axios(config);
    if (first?.data?.status !== REQUIRED_STATUS) return first;
    return new Promise((resolve, reject) => {
        ctx.flowRef.current = { config, resolve, reject };
        ctx.setError(null);
        ctx.setOpen(true);
    });
}

/**
 * Closes the modal and resolves the pending flow with the response.
 * @param {Object} ctx - { setOpen, flowRef }
 * @param {Object} flow - The active flow holding the resolve callback
 * @param {Object} res - The final axios response
 * @returns {void}
 */
function finishFlow(ctx, flow, res) {
    ctx.setOpen(false);
    ctx.flowRef.current = null;
    flow.resolve(res);
}

/**
 * Handles a failed replay: keeps the modal open for retryable codes, otherwise
 * closes the modal and rejects the flow so the caller can surface the error.
 * @param {Object} error - The axios error
 * @param {Object} ctx - { setOpen, setError, flowRef }
 * @param {Object} flow - The active flow holding the reject callback
 * @returns {void}
 */
function handleReplayError(error, ctx, flow) {
    const code = resolveFailureCode(error);
    if (code === "invalid_code" || code === "code_expired") {
        ctx.setError(failureMessage(code));
        return;
    }
    ctx.setOpen(false);
    ctx.flowRef.current = null;
    flow.reject(error);
}

/**
 * Submits the entered code by replaying the stored request.
 * @param {string} code - The OTP code
 * @param {Object} ctx - { flowRef, setSubmitting, setError, setOpen }
 * @returns {Promise<void>}
 */
async function handleCodeSubmit(code, ctx) {
    const flow = ctx.flowRef.current;
    if (!flow || !code) return;
    ctx.setSubmitting(true);
    ctx.setError(null);
    try {
        finishFlow(ctx, flow, await replayWithCode(flow.config, code));
    } catch (error) {
        handleReplayError(error, ctx, flow);
    } finally {
        ctx.setSubmitting(false);
    }
}

/**
 * Re-runs the original request without a code to trigger a fresh OTP email.
 * @param {Object} flowRef - Ref holding the active flow
 * @param {Function} setError - Setter for the inline status message
 * @returns {Promise<void>}
 */
async function resendCode(flowRef, setError) {
    const flow = flowRef.current;
    if (!flow) return;
    try {
        await axios(flow.config);
        setError("Un nouveau code a été envoyé par email.");
    } catch (error) {
        setError("Impossible de renvoyer le code.");
    }
}

export { startFlow, handleCodeSubmit, resendCode };
